import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n/useT';
import type { XkEvent } from '@/lib/api/types';

/**
 * PRD Xotski v2.0 §7.1 — "My Next Event" card on Entity Home.
 *
 * Picks the single upcoming event closest to today. Brand logo on a
 * color block (brand context comes from the page wrapper), event name,
 * date, location, and a primary CTA into the event detail.
 *
 * When no upcoming event exists the card collapses into a "create your
 * first event" placeholder. Multiple-event display is the job of the
 * UpcomingEventsList that sits below this card.
 */
export function MyNextEventCard({ brandColor }: { brandColor?: string }) {
  const t = useT();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.events.all,
    queryFn: () => api.list<XkEvent>('/xk-event'),
  });

  const nextEvent = useMemo(() => pickNextEvent(data?.items ?? []), [data]);

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="mt-3 h-10 w-64" />
        <Skeleton className="mt-2 h-4 w-32" />
      </Card>
    );
  }

  if (!nextEvent) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[var(--color-fg-muted)]">
          <CalendarDays className="h-4 w-4" />
          <span>{t('entityHome.nextEvent.label')}</span>
        </div>
        <p className="mt-2 text-lg font-semibold text-[var(--color-fg-primary)]">
          {t('entityHome.nextEvent.noneTitle')}
        </p>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('entityHome.nextEvent.noneHint')}
        </p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden p-0">
      <div
        className="px-6 py-5"
        style={{
          backgroundColor: brandColor ?? 'var(--color-xk-surface)',
          color: brandColor ? 'var(--color-fg-on-accent)' : undefined,
        }}
      >
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider opacity-80">
          <CalendarDays className="h-4 w-4" />
          <span>{t('entityHome.nextEvent.label')}</span>
        </div>
        <h3 className="mt-2 text-2xl font-bold">{nextEvent.name}</h3>
        <p className="mt-1 text-sm opacity-80">
          {formatEventWhen(nextEvent)}
        </p>
        {nextEvent.address && (
          <p className="text-sm opacity-80">{nextEvent.address}</p>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border-subtle)] p-3">
        <Button
          variant="ghost"
          onClick={() => navigate(`/events/${nextEvent.id}`)}
        >
          {t('entityHome.nextEvent.openCta')}
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

function pickNextEvent(events: XkEvent[]): XkEvent | null {
  const now = Date.now();
  const upcoming = events
    .filter((e) => {
      const start = e.startDate ? new Date(e.startDate).getTime() : 0;
      return start >= now;
    })
    .sort((a, b) => {
      const aStart = a.startDate ? new Date(a.startDate).getTime() : 0;
      const bStart = b.startDate ? new Date(b.startDate).getTime() : 0;
      return aStart - bStart;
    });
  return upcoming[0] ?? null;
}

function formatEventWhen(event: XkEvent): string {
  if (!event.startDate) return '';
  const d = new Date(event.startDate);
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
